import {getHoursAndMinutesFromTime} from 'platform/components';
import {z} from 'zod';

import i18n from '@dms/i18n';

import {parseDate} from 'shared/web';

/**
 * Extracted from UserAbsenceFormDialog so the schema can be unit-tested without
 * mounting the dialog: importing it from the component dragged in the RTK
 * mutations, `@dms/shared`, `platform/foundation` and React for a spec that only
 * calls `safeParse`. 13.5 s -> 7.5 s in CI (T20-133319).
 *
 * Not barrel-free yet, and that is the remaining cost: `getHoursAndMinutesFromTime`
 * comes from the `platform/components` barrel and `parseDate` from `shared/web`.
 * Cutting those needs a narrow entry point in the design system (or inlining the
 * ~6-line time parse) — a deliberate decision, not a cleanup.
 */
const getAbsenceDateTime = (date: string, time: string) => {
  const parsedDate = parseDate(date);
  const parsedTime = getHoursAndMinutesFromTime(time);

  if (!parsedDate || !parsedTime) {
    return null;
  }

  parsedDate.setHours(Number(parsedTime.hour), Number(parsedTime.minute), 0, 0); // eslint-disable-line eag/no-native-date-math -- Pre-existing native Date math, migrate to date-fns when possible

  return parsedDate;
};

export const getAbsenceApiDateTime = (date: string, time: string) => {
  const dateTime = getAbsenceDateTime(date, time);

  return dateTime ? dateTime.toISOString() : '';
};

const userAbsenceFormFieldsSchema = z.object({
  dateFrom: z.string().min(1, i18n.t('errors.field_is_required')),
  dateFromTime: z.string().min(1, i18n.t('errors.field_is_required')),
  dateTo: z.string().nullable(),
  dateToTime: z.string().nullable(),
  absenceContactUserId: z.string().nullable(),
  description: z.string().max(255, i18n.t('errors.field_is_too_long')).nullable(),
  forwardEmailsToDelegate: z.boolean(),
});

const addDateRangeValidation = <T extends z.ZodTypeAny>(schema: T) =>
  schema.superRefine((values, context) => {
    const dateFrom = getAbsenceDateTime(values.dateFrom, values.dateFromTime);
    const dateTo =
      values.dateTo && values.dateToTime
        ? getAbsenceDateTime(values.dateTo, values.dateToTime)
        : null;

    if (dateFrom && dateTo && dateTo.getTime() <= dateFrom.getTime()) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dateTo'],
        message: i18n.t('general.validations.fromTimeMustBeBeforeToTime'),
      });
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dateToTime'],
        message: i18n.t('general.validations.fromTimeMustBeBeforeToTime'),
      });
    }
  });

export const userAbsenceFormSchema = addDateRangeValidation(userAbsenceFormFieldsSchema);

export const userAbsenceAdminFormSchema = addDateRangeValidation(
  userAbsenceFormFieldsSchema.extend({
    userId: z.string().min(1, i18n.t('errors.field_is_required')),
  })
);
